/**
 * SyncSettings Component
 * Step 4: Configure datasource sync settings and finalize creation
 */

import React, { useState } from 'react';
import { validateForm, syncSettingsValidationSchema, hasErrors } from '../../utils/validation';

interface SyncSettingsProps {
  initialValues: {
    name: string;
    description: string;
    syncEnabled: boolean;
    syncFrequency: number;
    allowedRoles: string[];
  };
  onSubmit: (settings: {
    name: string;
    description: string;
    syncEnabled: boolean;
    syncFrequency: number;
    allowedRoles: string[];
  }) => void;
  onBack: () => void;
  saving: boolean;
}

const availableRoles = [
  { value: 'super_admin', label: 'Super Admin', description: 'Full system access' },
  { value: 'org_admin', label: 'Organization Admin', description: 'Organization management' },
  { value: 'ceo', label: 'CEO', description: 'Executive access' },
  { value: 'cfo', label: 'CFO', description: 'Financial access' },
  { value: 'coo', label: 'COO', description: 'Operations access' },
  { value: 'manager', label: 'Manager', description: 'Team management' },
  { value: 'employee', label: 'Employee', description: 'Standard access' },
];

const frequencyOptions = [
  { value: 5, label: '5 minutes' },
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 360, label: '6 hours' },
  { value: 720, label: '12 hours' },
  { value: 1440, label: '24 hours' },
];

export const SyncSettings: React.FC<SyncSettingsProps> = ({
  initialValues,
  onSubmit,
  onBack,
  saving,
}) => {
  const [formData, setFormData] = useState({
    name: initialValues.name || '',
    description: initialValues.description || '',
    syncEnabled: initialValues.syncEnabled !== undefined ? initialValues.syncEnabled : true,
    syncFrequency: initialValues.syncFrequency || 60,
    allowedRoles: initialValues.allowedRoles.length > 0 ? initialValues.allowedRoles : ['org_admin'],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleRoleToggle = (role: string) => {
    const newRoles = formData.allowedRoles.includes(role)
      ? formData.allowedRoles.filter((r) => r !== role)
      : [...formData.allowedRoles, role];
    handleChange('allowedRoles', newRoles);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    const validationErrors = validateForm(formData, syncSettingsValidationSchema);
    if (hasErrors(validationErrors)) {
      setErrors(validationErrors);
      return;
    }

    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h3 className="text-base font-extrabold text-gray-900 mb-1 tracking-tight">
          Sync Configuration
        </h3>
        <p className="text-gray-600 mb-6">
          Configure how this datasource will be used and synchronized
        </p>
      </div>

      {/* Name */}
      <div>
        <label className="lbl">
          Datasource Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          placeholder="Salesforce Production"
          className={`inp ${errors.name ? 'err' : ''}`}
        />
        {errors.name && (
          <p className="hint text-red-600">{errors.name}</p>
        )}
        <p className="hint">
          A friendly name to identify this datasource
        </p>
      </div>

      {/* Description */}
      <div>
        <label className="lbl">
          Description (Optional)
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Production Salesforce instance for sales data"
          rows={3}
          className="inp"
        />
        <p className="hint">
          Optional description of this datasource
        </p>
      </div>

      {/* Sync Enabled */}
      <div className="note">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="syncEnabled"
            checked={formData.syncEnabled}
            onChange={(e) => handleChange('syncEnabled', e.target.checked)}
            className="mt-1 w-4 h-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
          />
          <div className="flex-1">
            <label htmlFor="syncEnabled" className="block text-sm font-medium text-gray-900 cursor-pointer">
              Enable Automatic Sync
            </label>
            <p className="text-xs text-gray-600 mt-1">
              Automatically synchronize data from this datasource at regular intervals
            </p>
          </div>
        </div>

        {/* Frequency Selector (only if sync enabled) */}
        {formData.syncEnabled && (
          <div className="mt-4 pl-7">
            <label className="lbl">
              Sync Frequency <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.syncFrequency}
              onChange={(e) => handleChange('syncFrequency', parseInt(e.target.value))}
              className={`inp ${errors.syncFrequency ? 'err' : ''}`}
            >
              {frequencyOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.syncFrequency && (
              <p className="hint text-red-600">{errors.syncFrequency}</p>
            )}
            <p className="hint">
              How often to sync data from this datasource
            </p>
          </div>
        )}
      </div>

      {/* Access Control */}
      <div>
        <label className="lbl">
          Access Control <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-gray-600 mb-3">
          Select which roles can access this datasource
        </p>
        <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
          {availableRoles.map((role) => (
            <div
              key={role.value}
              className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded transition-colors"
            >
              <input
                type="checkbox"
                id={`role-${role.value}`}
                checked={formData.allowedRoles.includes(role.value)}
                onChange={() => handleRoleToggle(role.value)}
                className="mt-1 w-4 h-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
              />
              <div className="flex-1">
                <label htmlFor={`role-${role.value}`} className="block text-sm font-medium text-gray-900 cursor-pointer">
                  {role.label}
                </label>
                <p className="text-xs text-gray-500">{role.description}</p>
              </div>
            </div>
          ))}
        </div>
        {errors.allowedRoles && (
          <p className="mt-2 text-sm text-red-600">{errors.allowedRoles}</p>
        )}
        <p className="hint">
          {formData.allowedRoles.length} role(s) selected
        </p>
      </div>

      {/* Summary */}
      <div className="note">
        <h4 className="font-medium text-cyan-900 mb-2">Summary</h4>
        <ul className="text-sm text-cyan-800 space-y-1">
          <li>• Name: {formData.name || '(Not set)'}</li>
          <li>
            • Sync: {formData.syncEnabled ? `Every ${frequencyOptions.find(o => o.value === formData.syncFrequency)?.label}` : 'Disabled'}
          </li>
          <li>• Access: {formData.allowedRoles.length} role(s)</li>
        </ul>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-4">
        <button
          type="button"
          onClick={onBack}
          disabled={saving}
          className="btn btn-g"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={saving}
          className="btn btn-b flex-1"
        >
          {saving ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            'Save & Activate'
          )}
        </button>
      </div>
    </form>
  );
};
